import { useCurrentPath } from "@/hooks";
import { useDataStore, useFilterStore, useLocaleStore } from "@/stores";
import useExportStore from "@/stores/useExportStore";
import { useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import ContentEditable from "react-contenteditable";
import { generatePath, Link, useParams } from "react-router-dom";
import Settings from "./Settings";

interface ButtonProps {
    text: string;
    href?: string;
    internalPageId?: string;
    internalElementSlug?: string;
    target?: "_blank" | "_self" | "_parent" | "_top";
    style: string;
    size: string;
    action: string | null;
}

const StepButton = ({
    text,
    href,
    internalPageId,
    internalElementSlug,
    target,
    style = "default",
    size = "large",
    action = null,
}: Partial<ButtonProps>) => {
    const userPages = useDataStore((state) => state.pages);
    const userElements = useDataStore((state) => state.elements);
    const software = useDataStore((state) => state.software);

    const activeVersion = useFilterStore((state) => state.activeVersion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);

    const createExport = useExportStore((state) => state.createExport);

    const [link, setLink] = useState<{ link: string; target: string } | null>({ link: "#", target: "_self" });
    const [img, setImg] = useState<string | null>(null);

    const editableRef = useRef<HTMLDivElement | null>(null);

    const {
        connectors: { connect },
        actions: { setProp },
    } = useNode((node) => ({
        parentNodeId: node.data.parent,
    }));
    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));
    const params = useParams();
    const currentPath = useCurrentPath();

    useEffect(() => {
        // Fix for nested divs being editable when they shouldn't exist due to a previous bug with pasting content.
        if (!editableRef.current) return;

        const editableDivs = editableRef.current.querySelectorAll("div[contenteditable='true']");

        for (let i = 0; i < editableDivs.length; i++) {
            const editableDiv = editableDivs[i] as HTMLDivElement;
            editableDiv.contentEditable = enabled.toString();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    useEffect(() => {
        // Fix for pasted content sometimes having font-size attribute.
        if (!editableRef.current) return;

        const elementsWithFontSize = editableRef.current.querySelectorAll("[style*='font-size']");

        for (let i = 0; i < elementsWithFontSize.length; i++) {
            const element = elementsWithFontSize[i] as HTMLElement;
            element.style.fontSize = "";
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableRef.current, enabled]);

    useEffect(() => {
        if (enabled || !activeLanguage) return;

        fixInternalLink();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, enabled]);

    useEffect(() => {
        if (style.startsWith("software-") && activeVersion && software[activeVersion]) {
            const activeSoftware = software[activeVersion].filter((x) => x.active && !x.isIfc);
            const item = activeSoftware.find((x) => `software-${x.id}` === style);
            if (!item) return;

            setImg(item.icon);

            document.documentElement.style.setProperty(`--color-software-${item.id}`, item.color ?? "#05b692");

            let softwareStyle = document.getElementById(`style-software-${item.id}`);
            if (softwareStyle) document.getElementsByTagName("head")[0].removeChild(softwareStyle);

            softwareStyle = document.createElement("style");
            softwareStyle.id = `style-software-${item.id}`;
            softwareStyle.innerHTML = `
                    .software-${item.id} {
                        background-color: color-mix(in oklab, var(--color-software-${item.id}) 10%, transparent);
                        color: var(--color-software-${item.id});
                        &:is(:where(.group):has(*:checked) *) {
                            background-color: var(--color-software-${item.id});
                            color: white;
                        }
                    }

                    .software-${item.id}:hover {
                        background-color: color-mix(in oklab, var(--color-software-${item.id}) 90%, transparent);
                        color: white;
                    }

                    .software-${item.id}-selector {
                        border-color: rgba(0, 0, 0, 0.25);
                    }

                    .software-${item.id}-selector:hover {
                        border-color: rgba(0, 0, 0, 0.25);
                    }

                    .software-${item.id}-selected {
                        background-color: var(--color-software-${item.id});
                    }

                    ${
                        item.invert &&
                        `.software-${item.id} img {
                        &:is(:where(.group):has(*:checked) *) {
                            filter: invert(1);
                        }
                    }
                    .software-${item.id}:hover img {
                        filter: invert(1);
                    }`
                    }
                `;
            document.getElementsByTagName("head")[0].appendChild(softwareStyle);
        }
    }, [style, activeVersion, software]);

    const fixInternalLink = () => {
        const pageId = internalPageId;
        const elementSlug = internalElementSlug;
        const link = { link: "#", target: target || "_self" };
        if (pageId) {
            const page = userPages.find((page) => page.id === pageId);
            if (page) {
                link.link = generatePath(currentPath, { ...params, "*": page.url }).replace(":/", "");
            }
        } else if (elementSlug) {
            const elementPage = userElements[activeVersion!]?.find((page) => page.slug === elementSlug);
            if (elementPage) {
                link.link = generatePath(currentPath, { ...params, "*": `elements/${elementSlug}` }).replace(":/", "");
            }
        } else if (href) {
            link.link = href;
        } else {
            setLink(null);
            return;
        }

        setLink(link);
    };

    if (link)
        return (
            <Link
                to={link.link}
                target={link.target}
                ref={(ref) => connect(ref!)}
                className={clsx([
                    "flex rounded-md text-base font-medium items-center no-underline",
                    enabled ? "cursor-auto" : "cursor-pointer",
                    {
                        "h-[90px] px-7.5 py-5 bg-primary-100 text-primary-500 place-content-center hover:bg-primary-600 hover:text-white":
                            style === "default",
                        "bg-primary-500 text-white gap-2 text-l justify-center px-7.5 py-5 shrink-0 hover:bg-primary-600":
                            style === "action",
                        "flex-wrap min-h-[48px] gap-3 px-4 py-0": style.startsWith("software-"),
                        [style]: style.startsWith("software-"),
                    },
                    {
                        "h-[90px]": size === "large",
                        "h-[48px]": size === "small",
                    },
                ])}
            >
                {img && (
                    <div className="flex-shrink-0">
                        <img
                            src={img}
                            className={clsx(["w-auto", size === "large" && "h-[50px]", size === "small" && "h-[30px]"])}
                        />
                    </div>
                )}

                <ContentEditable
                    html={text || ""}
                    innerRef={editableRef}
                    disabled={!enabled}
                    onChange={(e) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setProp((props: any) => (props.text = e.target.value), 500);
                    }}
                    tagName="span"
                    className="font-medium text-base/normal"
                />
            </Link>
        );

    return (
        <button
            ref={(ref) => connect(ref!)}
            className={clsx([
                "flex rounded-md text-base font-medium items-center no-underline",
                enabled ? "cursor-auto" : "cursor-pointer",
                {
                    "h-[90px] px-7.5 py-5 bg-primary-100 text-primary-500 place-content-center hover:bg-primary-600 hover:text-white":
                        style === "default",
                    "bg-primary-500 text-white gap-2 text-l justify-center px-7.5 py-5 shrink-0 hover:bg-primary-600":
                        style === "action",
                    "flex-wrap min-h-[48px] gap-3 px-4 py-0": style.startsWith("software-"),
                    [style]: style.startsWith("software-"),
                },
                {
                    "h-[90px]": size === "large",
                    "h-[48px]": size === "small",
                },
            ])}
            onClick={() => {
                switch (action) {
                    case "export-excel":
                        createExport();
                        break;

                    default:
                        break;
                }
            }}
        >
            {img && (
                <div className="flex-shrink-0">
                    <img
                        src={img}
                        className={clsx(["w-auto", size === "large" && "h-[50px]", size === "small" && "h-[30px]"])}
                    />
                </div>
            )}

            <ContentEditable
                html={text || ""}
                innerRef={editableRef}
                disabled={!enabled}
                onChange={(e) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setProp((props: any) => (props.text = e.target.value), 500);
                }}
                tagName="span"
                className="font-medium text-base/normal"
            />
        </button>
    );
};

export default StepButton;

StepButton.craft = {
    displayName: "Button",
    props: {
        text: "Text",
        style: "default",
        size: "large",
    },
    related: {
        settings: Settings,
    },
    rules: {
        canDrag: () => true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) => targetNode.data.name === "StepButtonContainer",
    },
};
